import React from 'react';
import { LandingNavbar, LandingFooter } from '@/components/landing';
import { useCal } from '@/hooks/useCal';
import { 
  Search,
  AlertTriangle,
  Eye,
  Zap,
  Filter,
  RefreshCw,
  Crosshair,
  BarChart2,
  Share2,
  MoreHorizontal
} from 'lucide-react';

// --- ReflowAI Design System (Strict Blue/Sapphire Theme) ---
const COLORS = {
  brilliance: '#FCFDFD',      // Main Background
  glass: 'rgba(255, 255, 255, 0.65)',
  glassBorder: 'rgba(255, 255, 255, 0.8)',
  sapphire: '#2F7EDA',        // Primary Brand Color
  midnight: '#0F172A',        // Headings
  slate: '#555663',           // Body Text
  red: '#FF5F57',             // Danger
  green: '#28C840',           // Success
  orange: '#FEBC2E',          // Warning
  purple: '#5A2E98',          // Competitor A
  teal: '#00C7BE',            // Competitor B
};

// --- Global Styles ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

    :root {
      --font-primary: 'Plus Jakarta Sans', sans-serif;
    }

    body {
      font-family: var(--font-primary);
      color: ${COLORS.slate};
      background-color: ${COLORS.brilliance};
      overflow-x: hidden;
    }

    /* Soft Gradient Background */
    .mesh-bg {
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%; z-index: -1;
      background: 
        radial-gradient(at 0% 0%, rgba(47, 126, 218, 0.08) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(254, 188, 46, 0.05) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(47, 126, 218, 0.03) 0px, transparent 50%);
      pointer-events: none;
    }

    /* Glass Panels */
    .glass-panel {
      background: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.7);
      box-shadow: 0 8px 32px rgba(47, 126, 218, 0.03);
    }

    .glass-card {
      background: rgba(237, 239, 243, 0.5);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(198, 209, 215, 0.4);
    }

    /* Mobile Mockup Scrollbar */
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    /* Animations */
    @keyframes float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-5px); }
    }
    .animate-float { animation: float 6s ease-in-out infinite; }
    
    /* Radar Chart Transitions */
    .radar-polygon { transition: all 1s cubic-bezier(0.4, 0, 0.2, 1); }

    /* Halo Background Gradients */
    .halo-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      overflow: hidden;
      pointer-events: none;
    }

    .halo-gradient {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      animation: halo-pulse 8s ease-in-out infinite;
    }

    @keyframes halo-pulse {
      0%, 100% { transform: scale(1); opacity: 0.3; }
      50% { transform: scale(1.1); opacity: 0.5; }
    }

    /* Glass Orbs */
    .glass-orb {
      position: absolute;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.5);
      animation: float 6s ease-in-out infinite;
      pointer-events: none;
    }
  `}</style>
);

// --- Components ---
// --- KPI Tile Component ---
const StatTile = ({ label, value, trend, trendVal, icon: Icon, color }: { label: string; value: string; trend: 'up' | 'down'; trendVal: string; icon: React.ComponentType<any>; color: string }) => {
  const ArrowUp = () => (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7m0 0H7m10 0v10" />
    </svg>
  );
  
  const ArrowDown = () => (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l10 10m0 0H7m10 0V7" />
    </svg>
  );

  return (
    <div className="glass-panel p-3 sm:p-4 md:p-6 rounded-[20px] sm:rounded-[24px] md:rounded-[32px] flex items-center justify-between group transition-shadow duration-200">
      <div className="min-w-0 flex-1 pr-2">
        <div className="text-[#6B7280] text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-1 sm:mb-2 truncate">{label}</div>
        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A] mb-0.5 sm:mb-1">{value}</div>
        <div className={`flex items-center gap-1 text-[10px] sm:text-xs font-bold ${trend === 'up' ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
           {trend === 'up' ? <ArrowUp /> : <ArrowDown />}
           <span className="truncate">{trendVal}</span>
        </div>
      </div>
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg flex-shrink-0`} style={{ backgroundColor: color }}>
         <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
    </div>
  );
};

// --- 1. Shelf Simulator (The "Blinkit View") ---
const ShelfSimulator = () => {
  const items = [
    { type: 'ad', brand: 'Competitor X', name: 'Oat Milk Classic', price: '₹299', img: '/Oat Milk/30791036-6583-414e-b646-9714e5764494_8ZO1AMOYID_MN_16122025.avif', tag: 'Sponsored' },
    { type: 'organic', brand: 'Competitor Y', name: 'Creamy Oat 1L', price: '₹240', img: '/Oat Milk/4_6d04c3f3-a936-4e29-b53a-25523e0f5216.webp', rank: 1 },
    { type: 'organic', brand: 'Big Brand Z', name: 'Barista Edition', price: '₹310', img: '/Oat Milk/7130yhOMdqL.jpg', rank: 2 },
    { type: 'your-product', brand: 'Reflow (You)', name: 'Premium Oat Milk', price: '₹280', img: '/Oat Milk/71j9ofLt7BL._AC_UF350,350_QL80_.jpg', rank: 3, alert: 'Low CTR' },
    { type: 'organic', brand: 'Local Co', name: 'Cheap Oats', price: '₹190', img: '/Oat Milk/4_6d04c3f3-a936-4e29-b53a-25523e0f5216.webp', rank: 4 },
  ];

  return (
    <div className="glass-panel rounded-[32px] sm:rounded-[40px] p-1 h-full flex flex-col relative overflow-hidden group">
      {/* Phone Bezel */}
      <div className="absolute inset-2 border-2 sm:border-4 border-[#EDEFF3] rounded-[24px] sm:rounded-[32px] pointer-events-none z-20"></div>
      
      {/* App Header */}
      <div className="bg-[#F8CB46] h-20 sm:h-24 rounded-t-[24px] sm:rounded-t-[32px] pt-6 sm:pt-8 px-4 sm:px-6 relative z-10">
        <div className="flex items-center gap-3 bg-white p-2 rounded-xl shadow-sm">
           <Search className="w-4 h-4 text-gray-400" />
           <span className="text-xs font-bold text-gray-700">"oat milk"</span>
        </div>
      </div>

      {/* Scrollable Feed */}
      <div className="flex-1 bg-[#F5F7FA] overflow-y-auto hide-scrollbar px-3 sm:px-4 pt-3 sm:pt-4 pb-8 sm:pb-12 rounded-b-[24px] sm:rounded-b-[32px]">
        <div className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 sm:mb-3 text-center">Live Crawl: Indiranagar</div>
        
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {items.map((item, i) => (
            <div key={i} className={`bg-white p-3 rounded-xl shadow-sm relative flex flex-col ${item.type === 'your-product' ? 'ring-2 ring-[#2F7EDA] ring-offset-2' : ''}`}>
              
              {/* Badges */}
              {item.tag && <span className="absolute top-2 left-2 text-[8px] font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{item.tag}</span>}
              {item.rank && <span className="absolute top-2 left-2 text-[8px] font-bold bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">#{item.rank}</span>}
              
              {/* Product Image */}
              <div className="h-20 bg-gray-50 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                <img src={item.img} alt={item.name} className="w-full h-full object-cover" />
              </div>

              {/* Details */}
              <div className="mt-auto">
                <div className="text-[10px] text-gray-500 font-bold">{item.brand}</div>
                <div className="text-xs font-bold text-gray-800 leading-tight mb-1">{item.name}</div>
                <div className="flex justify-between items-center">
                   <div className="text-xs font-bold text-gray-900">{item.price}</div>
                   <div className="w-12 h-6 rounded-md border border-[#28C840] text-[#28C840] text-[10px] font-bold flex items-center justify-center uppercase">Add</div>
                </div>
              </div>

              {/* Your Product Pulse */}
              {item.type === 'your-product' && (
                <div className="absolute -right-2 -top-2 w-6 h-6 bg-[#2F7EDA] rounded-full flex items-center justify-center text-white z-20 shadow-lg animate-bounce">
                   <Eye className="w-3 h-3" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Floating Insight */}
      <div className="absolute bottom-6 left-6 right-6 bg-[#0F172A] text-white p-4 rounded-2xl z-30 shadow-2xl backdrop-blur-md bg-opacity-90">
         <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#FEBC2E] flex-shrink-0" />
            <div>
              <div className="text-xs font-bold text-[#6B7280] uppercase">Rank Alert</div>
              <div className="text-sm font-semibold">You dropped to <span className="text-[#FEBC2E]">Rank #3</span>. Competitor X bought the top ad slot today.</div>
            </div>
         </div>
      </div>
    </div>
  );
};

// --- 2. Keyword Scatter ---
const KeywordScatter = () => {
  const keywords = [
    { label: 'Oat Milk', x: 85, y: 92, color: COLORS.green, size: 'large' },
    { label: 'Healthy Snacks', x: 75, y: 35, color: COLORS.orange, size: 'medium' },
    { label: 'Protein Bar', x: 60, y: 80, color: COLORS.sapphire, size: 'small' },
    { label: 'Vegan Milk', x: 45, y: 15, color: COLORS.red, size: 'small' },
    { label: 'Breakfast', x: 92, y: 10, color: COLORS.red, size: 'large' },
    { label: 'Almond Butter', x: 30, y: 65, color: COLORS.sapphire, size: 'small' }
  ];

  return (
    <div className="glass-panel rounded-[32px] sm:rounded-[40px] p-4 sm:p-6 md:p-8 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">Keyword Battleground</h3>
          <p className="text-xs sm:text-sm text-[#555663]">Optimization Priorities</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 rounded-md border border-green-100">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-[9px] sm:text-[10px] font-bold text-green-700">Dominating</span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 rounded-md border border-orange-100">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-[9px] sm:text-[10px] font-bold text-orange-700">Opportunity</span>
          </div>
        </div>
      </div>

      <div className="flex-1 relative bg-white/40 rounded-xl sm:rounded-2xl p-4 sm:p-6 min-h-[250px] sm:min-h-[300px]">
        {/* Quadrants */}
        <div className="absolute inset-6 grid grid-cols-2 grid-rows-2 gap-px">
          <div className="bg-blue-50/50 rounded-tl-xl"></div>
          <div className="bg-green-50/50 rounded-tr-xl"></div>
          <div className="bg-gray-50/50 rounded-bl-xl"></div>
          <div className="bg-orange-50/50 rounded-br-xl"></div>
        </div>

        {/* Axes */}
        <div className="absolute inset-6 border-l-2 border-b-2 border-gray-300"></div>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-500">
          SEARCH VOLUME →
        </div>
        <div className="absolute left-3 top-1/2 -translate-y-1/2 -rotate-90 text-xs font-bold text-gray-500">
          VISIBILITY SCORE →
        </div>

        {/* Keywords */}
        {keywords.map((k, i) => (
          <div
            key={i}
            className="absolute group cursor-pointer"
            style={{
              left: `${6 + (k.x / 100) * 82}%`,
              bottom: `${6 + (k.y / 100) * 82}%`,
              transform: 'translate(-50%, 50%)'
            }}
          >
            <div
              className={`rounded-full transition-all duration-300 group-hover:scale-125 shadow-lg`}
              style={{
                backgroundColor: k.color,
                width: k.size === 'large' ? '40px' : k.size === 'medium' ? '30px' : '24px',
                height: k.size === 'large' ? '40px' : k.size === 'medium' ? '30px' : '24px',
                opacity: 0.8
              }}
            ></div>
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#0F172A] text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap pointer-events-none">
              <div className="font-bold">{k.label}</div>
              <div className="text-[10px] text-gray-400">Vol: {k.x} • Vis: {k.y}</div>
            </div>
          </div>
        ))}

        {/* Labels */}
        <div className="absolute top-8 right-8 text-xs font-bold text-green-700 opacity-70">
          DOMINATING
        </div>
        <div className="absolute bottom-8 right-8 text-xs font-bold text-orange-700 opacity-70">
          ATTACK ZONE
        </div>
      </div>
    </div>
  );
};

// --- 3. Competitor Radar ---
const CompetitorRadar = () => {
  const metrics = ['Rank', 'Price', 'Reviews', 'Speed', 'Ads'];
  const dataYou = [85, 90, 75, 60, 40];
  const dataComp = [65, 50, 85, 80, 95];
  
  const size = 240;
  const center = size / 2;
  const radius = 80;
  const totalAxes = metrics.length;

  // Calculate coordinates for radar chart
  const getCoordinates = (index: number, value: number) => {
    const angle = (Math.PI * 2 * index) / totalAxes - Math.PI / 2;
    const x = center + (value / 100) * radius * Math.cos(angle);
    const y = center + (value / 100) * radius * Math.sin(angle);
    return { x, y };
  };

  // Generate path for polygon
  const generatePath = (data: number[]) => {
    const points = data.map((val, i) => {
      const { x, y } = getCoordinates(i, val);
      return `${x},${y}`;
    });
    return points.join(' ');
  };

  // Grid levels
  const gridLevels = [25, 50, 75, 100];

  return (
    <div className="glass-panel rounded-[32px] sm:rounded-[40px] p-4 sm:p-6 md:p-8 h-full flex flex-col">
      <div className="flex justify-between items-start mb-3 sm:mb-4">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">Share of Voice</h3>
          <p className="text-xs sm:text-sm text-[#555663]">Reflow (You) vs Market Leader</p>
        </div>
      </div>

      <div className="flex-1 w-full h-full flex items-center justify-center min-h-[200px]">
        <svg width="100%" height="200" viewBox={`0 0 ${size} ${size}`} className="overflow-visible max-h-[240px]">
          
          {/* Grid (Concentric Polygons) */}
          {gridLevels.map((level, i) => (
            <polygon
              key={i}
              points={generatePath(Array(totalAxes).fill(level))}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="1"
            />
          ))}

          {/* Axis Lines */}
          {metrics.map((axis, i) => {
            const { x, y } = getCoordinates(i, 100);
            return (
              <g key={i}>
                <line x1={center} y1={center} x2={x} y2={y} stroke="#E2E8F0" strokeWidth="1" />
                {/* Axis Labels */}
                <text
                  x={x + (x - center) * 0.2}
                  y={y + (y - center) * 0.2 + 4}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="bold"
                  fill="#64748B"
                >
                  {axis}
                </text>
              </g>
            );
          })}

          {/* Data: Competitor (Red) */}
          <polygon
            points={generatePath(dataComp)}
            fill="#FF5F57"
            fillOpacity="0.2"
            stroke="#FF5F57"
            strokeWidth="2"
            className="transition-all duration-1000 ease-out"
          />

          {/* Data: You (Blue) */}
          <polygon
            points={generatePath(dataYou)}
            fill="#2F7EDA"
            fillOpacity="0.3"
            stroke="#2F7EDA"
            strokeWidth="2"
            className="transition-all duration-1000 ease-out"
          />

          {/* Dots for You */}
          {dataYou.map((val, i) => {
            const { x, y } = getCoordinates(i, val);
            return <circle key={i} cx={x} cy={y} r="3" fill="#2F7EDA" stroke="white" strokeWidth="1" />;
          })}

          {/* Dots for Competitor */}
          {dataComp.map((val, i) => {
            const { x, y } = getCoordinates(i, val);
            return <circle key={`comp-${i}`} cx={x} cy={y} r="3" fill="#FF5F57" stroke="white" strokeWidth="1" />;
          })}
        </svg>
      </div>

      <div className="flex justify-center gap-8 mt-4">
        <div className="flex items-center gap-2 text-xs font-bold text-[#0F172A]">
          <div className="w-3 h-3 bg-[#2F7EDA] rounded-full shadow-sm border border-white"></div> You (Organic Leader)
        </div>
        <div className="flex items-center gap-2 text-xs font-bold text-[#0F172A]">
          <div className="w-3 h-3 bg-[#FF5F57] rounded-full shadow-sm border border-white"></div> Comp X (Ad Heavy)
        </div>
      </div>
    </div>
  );
};

// --- Why Visibility Matters Section ---
const WhyVisibilityMatters = () => {
  return (
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto glass-panel rounded-[48px] p-12 bg-white/40">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-4">Why Visibility Matters</h2>
          <p className="text-[#555663] max-w-2xl mx-auto">
            90% of Quick Commerce purchases come from the first page of search results. If you're not visible, you're invisible to customers.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { 
              icon: Search, 
              title: "Search Rank Decay", 
              desc: "Your organic rankings drop every time a competitor gets more clicks or sales. We track rank changes hourly so you never miss a shift." 
            },
            { 
              icon: Eye, 
              title: "Share of Voice", 
              desc: "Know exactly what percentage of search results you own vs competitors for every keyword that matters to your brand." 
            },
            { 
              icon: Crosshair, 
              title: "Keyword Opportunities", 
              desc: "Discover high-volume keywords where you're underperforming. Find gaps in competitor coverage you can exploit." 
            }
          ].map((feature, i) => (
            <div key={i} className="text-center p-6 rounded-3xl hover:bg-white/50 transition-colors">
              <div className="w-16 h-16 rounded-2xl bg-[#EDE9FE] flex items-center justify-center mx-auto mb-6 text-[#7C3AED]">
                <feature.icon className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-3">{feature.title}</h3>
              <p className="text-[#555663] leading-relaxed text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Component ---
const VisibilityCTA = () => {
  const { openCalModal } = useCal();

  return (
    <section className="py-20 px-4 text-center">
      <div className="max-w-4xl mx-auto glass-panel rounded-[60px] p-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#2F7EDA] to-[#5a9ef5]"></div>
        
        <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-8">Master Your Digital Shelf Visibility</h2>
        <p className="text-lg text-[#555663] mb-10 max-w-2xl mx-auto">Get real-time insights into your search rankings, keyword performance, and share of voice.</p>
        
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button 
            onClick={openCalModal}
            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide bg-[#2F7EDA] text-white hover:opacity-90 hover:-translate-y-0.5"
            style={{ boxShadow: '0 10px 30px -5px rgba(47, 126, 218, 0.4)' }}
          >
            Schedule a Call
          </button>
          <button 
            onClick={openCalModal}
            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide bg-white/50 text-[#555663] border border-[#C6D1D7] hover:opacity-90 hover:-translate-y-0.5"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            Book a Demo
          </button>
        </div>
      </div>
    </section>
  );
};

export function VisibilityLandingPage() {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleLiveCrawl = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  return (
    <>
      <GlobalStyles />
      
      {/* Halo Background Gradients */}
      <div className="halo-container">
        <div className="halo-gradient w-[800px] h-[800px] bg-[#2F7EDA] top-[-20%] left-[-10%] opacity-15"></div>
        <div className="halo-gradient w-[600px] h-[600px] bg-[#FEBC2E] bottom-[-10%] right-[-10%] opacity-12"></div>
        <div className="halo-gradient w-[700px] h-[700px] bg-[#5A2E98] top-[40%] left-[30%] opacity-10"></div>
        <div className="halo-gradient w-[500px] h-[500px] bg-[#2F7EDA] bottom-[30%] left-[-5%] opacity-8"></div>
      </div>

      {/* Main Content Layer (Scrollable) */}
      <div className="min-h-screen relative overflow-x-hidden">
        <LandingNavbar />

        <main className="overflow-x-hidden">
        <div className="pt-24 sm:pt-28 px-4 sm:px-6 md:px-8 max-w-[1400px] mx-auto pb-8 sm:pb-12">
          
          {/* Header Action Bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 sm:mb-10 md:mb-12 gap-4 sm:gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-bold uppercase tracking-wider mb-3 sm:mb-4">
                  <Eye className="w-3 h-3" /> Digital Shelf Simulator
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F172A] tracking-tight mb-2">
                Visibility Command Center
              </h1>
              <p className="text-[#555663] text-base sm:text-lg">
                Simulate customer journeys and reclaim lost rankings.
              </p>
            </div>
            
            {/* Mobile buttons */}
            <div className="flex md:hidden gap-2 w-full">
              <button className="bg-white hover:bg-gray-50 text-[#0F172A] px-4 py-2 rounded-full font-bold text-xs shadow-sm transition-colors border border-[#EDEFF3] flex items-center gap-2 flex-1 justify-center">
                <Filter className="w-3 h-3" /> Filters
              </button>
              <button 
                onClick={handleLiveCrawl}
                disabled={isLoading}
                className="bg-[#0F172A] hover:bg-slate-800 text-white px-4 py-2 rounded-full font-bold text-xs shadow-lg transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed flex-1 justify-center"
              >
                <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} /> 
                {isLoading ? 'Syncing...' : 'Live Crawl'}
              </button>
            </div>
            
            {/* Desktop buttons */}
            <div className="hidden md:flex gap-3">
              <button className="bg-white hover:bg-gray-50 text-[#0F172A] px-5 py-2.5 rounded-full font-bold text-sm shadow-sm transition-colors border border-[#EDEFF3] flex items-center gap-2">
                <Filter className="w-4 h-4" /> Filters
              </button>
              <button 
                onClick={handleLiveCrawl}
                disabled={isLoading}
                className="bg-[#0F172A] hover:bg-slate-800 text-white px-6 py-2.5 rounded-full font-bold text-sm shadow-lg transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} /> 
                {isLoading ? 'Syncing...' : 'Live Crawl'}
              </button>
            </div>
          </div>

          {/* Row 1: KPI Tiles */}
          <div className={`grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8 transition-opacity duration-500 ${isLoading ? 'opacity-50 animate-pulse' : 'opacity-100'}`}>
             <StatTile label="Share of Search" value="34%" trend="up" trendVal="+2.4%" icon={Crosshair} color={COLORS.sapphire} />
             <StatTile label="Avg Rank Position" value="#3.2" trend="down" trendVal="-0.4" icon={BarChart2} color={COLORS.orange} />
             <StatTile label="Category Share" value="18%" trend="up" trendVal="+1.1%" icon={Share2} color={COLORS.purple} />
             <StatTile label="Sponsored Share" value="12%" trend="down" trendVal="Overspending" icon={Zap} color={COLORS.red} />
          </div>

           {/* Row 2: The Core Visualization (Bento Grid Layout) */}
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-12 sm:mb-16 transition-opacity duration-500 ${isLoading ? 'opacity-50 animate-pulse' : 'opacity-100'}`}>
               
               {/* Left: The Simulator (Mobile View) */}
               <div className="lg:col-span-4 min-h-[400px] sm:min-h-[500px] md:min-h-[600px] lg:h-[820px]">
                  <ShelfSimulator />
               </div>

               {/* Right: Analytics & Strategy */}
               <div className="lg:col-span-8 flex flex-col gap-6 sm:gap-8 lg:h-[820px]">
                  
                  {/* Top Right: Scatter Map */}
                  <div className="flex-1 min-h-[300px] sm:min-h-[400px] lg:min-h-0">
                     <KeywordScatter />
                  </div>

                  {/* Bottom Right: Split Stats - Same height containers */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:h-[380px] lg:flex-shrink-0">
                     <div className="min-h-[350px] sm:min-h-[400px] lg:h-full">
                        <CompetitorRadar />
                     </div>
                     
                     {/* Mini Module: Trending Keywords List */}
                     <div className="glass-panel rounded-[32px] sm:rounded-[40px] p-4 sm:p-6 flex flex-col min-h-[350px] sm:min-h-[400px] lg:h-full">
                       <div className="flex justify-between items-center mb-4">
                          <h3 className="font-bold text-[#0F172A]">Trending Movers</h3>
                          <MoreHorizontal className="w-4 h-4 text-gray-400" />
                       </div>
                       <div className="flex-1 overflow-y-auto space-y-3 hide-scrollbar">
                          {[
                            { term: 'Vegan Milk', change: '+12%', sentiment: 'good' },
                            { term: 'Breakfast Bars', change: '-5%', sentiment: 'bad' },
                            { term: 'Unsweetened', change: '+8%', sentiment: 'good' },
                            { term: 'Keto Snacks', change: '+22%', sentiment: 'good' },
                            { term: 'Dairy Free', change: '+15%', sentiment: 'good' },
                            { term: 'Organic', change: '+6%', sentiment: 'good' },
                          ].map((t, i) => (
                            <div key={i} className="flex justify-between items-center p-3 rounded-2xl bg-[#F5F7FA] hover:bg-white transition-colors">
                               <span className="text-xs font-bold text-[#555663]">{t.term}</span>
                               <span className={`text-xs font-bold ${t.sentiment === 'good' ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                                 {t.change}
                               </span>
                            </div>
                          ))}
                       </div>
                    </div>
                 </div>
              </div>
           </div>

        </div>
        </main>
      </div>

      <WhyVisibilityMatters />

      <VisibilityCTA />

      <LandingFooter />
    </>
  );
}
